/**
 * Pure helpers for interpreting API errors thrown by lib/api.ts.
 *
 * Kept in their own module (no React/JSX) so they can be unit-tested directly
 * without a renderer — see tests/query-error-banner.test.ts. Consumed by
 * QueryErrorBanner to distinguish 403 access-boundaries from system faults.
 */
import { RBAC_STORE_OUTAGE_ERROR } from "../../shared/rbac-store-outage";
import { SHARE_WITHHELD_CODE } from "../../shared/share-withheld";

/** Pull the HTTP status off an ApiError-shaped error (set by lib/api.ts), or
 *  fall back to parsing the "API <status>: …" message prefix for older throws. */
export function errorStatus(error: unknown): number | undefined {
  const status = (error as { status?: number } | undefined)?.status;
  if (typeof status === "number") return status;
  const m = /^API (\d{3}):/.exec((error as Error | undefined)?.message ?? "");
  return m ? Number(m[1]) : undefined;
}

/** Best-effort friendly message from a JSON error body ({message|error}). */
export function friendlyAccessMessage(error: unknown): string | undefined {
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message?.replace(/^API \d{3}:\s*/, "");
  if (!body) return undefined;
  try {
    const j = JSON.parse(body) as { message?: string; error?: string };
    return j.message || j.error || undefined;
  } catch {
    return undefined;
  }
}

/** True when there is at least one failing query and EVERY failing query is a
 *  genuine access denial (isAccessDenial): an access boundary, not a system
 *  fault. */
export function allForbidden(errors: unknown[]): boolean {
  return errors.length > 0 && errors.every(isAccessDenial);
}

/**
 * #3112: one error is a real "your role may not see this" decision: a 403
 * that is not an RBAC-store outage wearing a 403 (isRbacStoreOutage). The
 * test every surface uses to pick the calm role note over the fault box, so
 * a page where ONE read is refused and the rest answered shows the refusal
 * where that read would have been, not a blank.
 */
export function isAccessDenial(error: unknown): boolean {
  return errorStatus(error) === 403 && !isRbacStoreOutage(error);
}

/**
 * src/server/middleware/rbac.ts's catch turns an RBAC-store outage (a D1
 * blip on the role lookup) into a 403 with this exact body, fail-closed
 * (#327), and since #4564 the Ostendo capability authoriser answers its own
 * store faults (role, grant or override read) with the same body. That 403
 * is a system fault wearing an access-boundary's status code, not a real
 * "you may not see this" decision, so it must NEVER be treated as one: a
 * shared-widget helper that silences every 403 would hide an outage from
 * every role at once. Checked on the `error` field (not the whole message)
 * so a genuine access-denied body that happens to share wording never
 * matches by accident.
 */
export function isRbacStoreOutage(error: unknown): boolean {
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message?.replace(/^API \d{3}:\s*/, "");
  if (!body) return false;
  try {
    const j = JSON.parse(body) as { error?: string };
    return j.error === RBAC_STORE_OUTAGE_ERROR;
  } catch {
    return false;
  }
}

/**
 * #3669 PI503: the server's typed Ostendo outage (lib/ostendo-unavailable.ts,
 * a 503 whose body carries `error_code: "ostendo_unavailable"`). Returns the
 * company that is down (`source`, null when the body names none), or null
 * for any other error. Keyed on the code, not the status: a plain 503 from
 * anywhere else is not this.
 */
export function ostendoUnavailable(error: unknown): { source: string | null } | null {
  if (errorStatus(error) !== 503) return null;
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message?.replace(/^API \d{3}:\s*/, "");
  if (!body) return null;
  try {
    const j = JSON.parse(body) as { error_code?: unknown; source?: unknown };
    if (j.error_code !== "ostendo_unavailable") return null;
    return { source: typeof j.source === "string" && j.source.trim() ? j.source.trim() : null };
  } catch {
    return null;
  }
}

/**
 * #3671 PI503B: the server's typed completeness refusal (#3019, a 503 whose
 * body carries `error_code: "ostendo_result_truncated"`): Ostendo answered but
 * hit its row ceiling, so the read would be partial. Returns the company whose
 * read was cut (`db`, null when the body names none), or null for any other
 * error. Keyed on the code, like `ostendoUnavailable`.
 */
export function ostendoTruncated(error: unknown): { db: string | null } | null {
  if (errorStatus(error) !== 503) return null;
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message?.replace(/^API \d{3}:\s*/, "");
  if (!body) return null;
  try {
    const j = JSON.parse(body) as { error_code?: unknown; db?: unknown };
    if (j.error_code !== "ostendo_result_truncated") return null;
    return { db: typeof j.db === "string" && j.db.trim() ? j.db.trim() : null };
  } catch {
    return null;
  }
}

/**
 * #4576 SHARENARROW: the shared-conversation read's refusal that names the
 * stored result it withheld and why (shared/share-withheld.ts, a 403 whose
 * body carries `code: "share_result_withheld"`). Returns the server's
 * sentence, or null for any other error, a role's plain 403 included. Keyed
 * on the code, like `ostendoUnavailable`.
 */
export function withheldShareMessage(error: unknown): string | null {
  if (errorStatus(error) !== 403) return null;
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message?.replace(/^API \d{3}:\s*/, "");
  if (!body) return null;
  try {
    const j = JSON.parse(body) as { code?: unknown; message?: unknown };
    if (j.code !== SHARE_WITHHELD_CODE) return null;
    return typeof j.message === "string" && j.message.trim() ? j.message.trim() : null;
  } catch {
    return null;
  }
}

/**
 * #3398: the session behind a read has ended (401), so a retry sends the same
 * missing cookie and fails the same way. The reader needs to sign in again,
 * not a fault box.
 */
export function isSignedOut(error: unknown): boolean {
  return errorStatus(error) === 401;
}

/** Query-ish shape the shared-widget helpers below read. */
interface ErrorQueryLike {
  isError: boolean;
  error: unknown;
}

/**
 * #2727: for a shared-widget query whose access boundary is a decided
 * client-side degrade, not a fault (/api/cases/sla-summary,
 * /api/intelligence/health-scorecard, /api/costs/summary on the home page):
 * never surface a banner or toast for a 403. Any other failure (a real
 * outage) still needs to be visible, same as slaBannerQuery in cases-ui.ts,
 * whose shape this generalises. An RBAC-store outage (isRbacStoreOutage)
 * rides in on a 403 too, so it is excluded from the silencing here: a
 * genuine access-denied 403 stays silent, an outage-shaped 403 still bangers.
 */
export function silentOn403(q: ErrorQueryLike): { isError: boolean; error: unknown } {
  const genuineAccessDenied = q.isError && isAccessDenial(q.error);
  return { isError: q.isError && !genuineAccessDenied, error: q.error };
}

/** Error thrown by the API wrappers, carrying the HTTP status + raw body so
 *  callers (e.g. QueryErrorBanner) can distinguish 403 access-boundaries from
 *  5xx/network faults without string-parsing the message. */
export interface ApiError extends Error {
  status: number;
  body: string;
}

/** The one place an ApiError is built, so every transport (fetch and the
 *  XHR upload path that reports progress) throws the same shape. */
export function makeApiError(status: number, body: string): ApiError {
  const err = new Error(`API ${status}: ${body}`) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

const MAX_REASON = 200;

const clip = (s: string) => (s.length > MAX_REASON ? `${s.slice(0, MAX_REASON - 1)}…` : s);

/** #4450 ERRCODE: the plain line for any 5xx that is not a timeout. */
export const SERVER_FAULT_REASON = "The server hit a problem. Try again, and if it keeps happening tell support.";

/**
 * #4450 ERRCODE: the HTTP status for support ("HTTP 500"), shown only inside a
 * collapsed "Error details" line, never in the sentence a reader acts on. Null
 * when the error carries no status (no connection, a thrown non-API error).
 */
export function errorDetail(error: unknown): string | null {
  const status = errorStatus(error);
  return status == null ? null : `HTTP ${status}`;
}

/**
 * One short line saying why a read or write failed, for the place the user is
 * already looking (#3216, shared app-wide since #3305). A 400/403/409 body
 * carries the server's own words; a 5xx or a timeout says so in plain words,
 * never "no permission" and never the bare status (#4450).
 */
export function readErrorReason(error: unknown): string {
  const status = errorStatus(error);
  // #3398: a 401's body is the auth middleware's wording, not a reason a
  // reader can act on. Say what happened; ReadFailed offers the way back.
  if (status === 401) return "Your sign-in has expired.";
  if (status === 408 || status === 504 || status === 522 || status === 524) {
    return "The server took too long to answer.";
  }
  // #4450 ERRCODE: a plant operator cannot act on "500". Say what happened and
  // what to do; the status sits under "Error details" (errorDetail) for support.
  if (status != null && status >= 500) return SERVER_FAULT_REASON;
  const friendly = friendlyAccessMessage(error);
  if (friendly) return clip(friendly);
  if (status === 403) return "Your role is not allowed to do this.";
  const body =
    (error as { body?: string } | undefined)?.body ??
    (error as Error | undefined)?.message ??
    String(error ?? "Unknown error");
  const text = String(body).trim();
  if (/failed to fetch|networkerror|load failed/i.test(text)) return "No connection to the server.";
  return clip(text || "Unknown error");
}
